import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { getLogger } from '@/logger/logger';
import { resolveFbclid } from '@/storage/fbclid.storage';
import { CartPurchaseTrack } from '@/types/cart.types';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';

const logger = getLogger('cart.purchase-track');

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : undefined;
}

function buildAttributionTrack(): CartPurchaseTrack {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const fbclid = resolveFbclid(params.get(PersistedUserNavigationSearchParams.FB_CLID));
  const fbc = readCookie('_fbc');
  const fbp = readCookie('_fbp');

  return {
    ...definedOnly({
      utmSource: params.get(PersistedUserNavigationSearchParams.UTM_SOURCE),
      utmMedium: params.get(PersistedUserNavigationSearchParams.UTM_MEDIUM),
      utmCampaign: params.get(PersistedUserNavigationSearchParams.UTM_CAMPAIGN),
      utmTerm: params.get(PersistedUserNavigationSearchParams.UTM_TERM),
      fbclid,
      fbc,
      fbp,
    }),
  };
}

function definedOnly(track: Record<string, string | null | undefined>): CartPurchaseTrack {
  return Object.fromEntries(
    Object.entries(track).filter(([, value]) => Boolean(value)),
  ) as CartPurchaseTrack;
}

async function buildAnalyticsTrack(): Promise<CartPurchaseTrack> {
  try {
    const posthogSessionId = await getAnalyticsPlugin().getCurrentSessionId();

    if (!posthogSessionId) {
      return {};
    }

    return { posthogSessionId, posthogEventInternalId: crypto.randomUUID() };
  } catch (error) {
    logger.error('Failed to read the analytics session for the cart purchase track', {
      cart: { event: 'analytics_session_failed' },
      error,
    });

    return {};
  }
}

export async function buildPurchaseTrack(): Promise<CartPurchaseTrack | undefined> {
  const purchaseTrack = { ...await buildAnalyticsTrack(), ...buildAttributionTrack() };

  return Object.keys(purchaseTrack).length > 0 ? purchaseTrack : undefined;
}
